import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { toolSearch } from "components/GlobalSearchPage/ToolSearchResults/utils";
import PaneLayout from "components/PaneLayout";
import ToolSnippet from "components/ToolSnippet";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import ToolsPageAll from "./ToolsPageAll";
import ToolsPageLayoutMobile from "./ToolsPageLayoutMobile";
import ToolsPageSectionLabel from "./ToolsPageSectionLabel";
import ToolsPageTabs, { AVAILABLE_TABS, TabType } from "./ToolsPageTabs";

const title =
  "Investment Tools like IPO Analyser, Holdco Monitor & more | Smartkarma";
const description =
  "Analyse IPOs, Holding Companies, M&A situations and more using Smartkarma's proprietery investment tools";

type ToolsPageLayoutProps = {};

const ToolsPageLayout = ({}: ToolsPageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();
  const tabQuery = (router.query.tab as TabType) || "explore";

  const tab = useMemo(
    () => AVAILABLE_TABS.find((t) => t !== "separator" && t.id === tabQuery),
    [tabQuery]
  );

  const { account: loggedInAccount } = useAuthentication();
  const { trackActivity } = useActivityTracker();

  const [section, setSection] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  const segments = useMemo(() => {
    if (tab === "separator" || !tab?.segments) {
      return [];
    }

    return tab.segments.filter(
      (s) =>
        (!section || section === `tools-for-${s.id}`) &&
        (!s.permission || loggedInAccount?.[s.permission]) &&
        !s.hideFor?.some((p) => loggedInAccount?.[p]) &&
        (s.id !== "last-visited" ||
          loggedInAccount?.interfaceSetting?.lastVisitedTools)
    );
  }, [loggedInAccount, section, tab]);

  const children = useMemo(() => {
    if (tab !== "separator" && tab?.segments) {
      return (
        <>
          {segments.map((s) => {
            return (
              <div key={s.id} id={`tools-for-${s.id}`} className="mb-[24px]">
                <div className="p-[15px] md:p-0 mt-[15px] first-of-type:mt-0">
                  <ToolsPageSectionLabel>
                    {s.sectionIcon} {s.name}
                  </ToolsPageSectionLabel>
                  <div className="mt-[15px]">
                    <ToolsPageAll type={s.id} />
                  </div>
                </div>
              </div>
            );
          })}
        </>
      );
    }

    return (
      <div className="p-[15px] md:p-[30px] min-h-[calc(100vh-170px)]">
        <ToolsPageAll type={tabQuery} />
      </div>
    );
  }, [tab, tabQuery, segments]);

  const isSearchActive = searchQuery.trim().length >= 3;

  const searchResults = useMemo(() => {
    if (!isSearchActive) return [];
    return toolSearch(searchQuery, loggedInAccount);
  }, [isSearchActive, searchQuery, loggedInAccount]);

  const renderSearch = useMemo(() => {
    return (
      <div className="mb-[16px]">
        <div className="relative max-w-[300px]">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-[12px] top-1/2 -translate-y-1/2 text-[13px] text-shady-gray pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tools..."
            className={twMerge(
              "w-full pl-[34px] pr-[36px] py-[8px]",
              "text-[13px] rounded-full",
              "border border-solid border-[#e4eaf0]",
              "bg-white focus:outline-none focus:border-[#0276be]"
            )}
          />
          {searchQuery && (
            <div
              className="flex items-center justify-center absolute right-[12px] top-1/2 -translate-y-1/2 w-[30px] h-[30px] rounded-full"
              onClick={() => setSearchQuery("")}
            >
              <FontAwesomeIcon
                icon={faTimes}
                className="text-[15px] text-shady-gray"
              />
            </div>
          )}
        </div>
        {isSearchActive && (
          <div className="flex flex-wrap gap-[8px] mt-[16px]">
            {searchResults.length > 0 ? (
              searchResults.map((t) => (
                <ToolSnippet tool={t} key={t.id} query={searchQuery} />
              ))
            ) : (
              <div className="text-[13px] text-shady-gray py-[20px] w-full text-center">
                No tools found for &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [isSearchActive, searchQuery, searchResults]);

  const renderTabs = useMemo(() => {
    if (tab !== "separator" && tab?.segments) {
      return (
        <div className="mb-[20px] mt-0 md:mt-[-30px]">
          <div className="flex space-x-[4px] overflow-x-auto">
            <div
              onClick={() => {
                setSection("");
              }}
              className={twMerge(
                "cursor-pointer",
                "border border-solid border-[#e4eaf0] rounded-full",
                "px-[16px] py-[8px]",
                "text-[13px] font-medium leading-[1]",
                !section
                  ? "bg-[#0276be]"
                  : "bg-[#0276be19] hover:bg-gray-gray/40",
                !section ? "text-white" : "",
                "flex items-center space-x-[6px]",
                "whitespace-nowrap"
              )}
            >
              <span>All tools</span>
            </div>
            {tab.segments
              .filter(
                (s) =>
                  (!s.permission || loggedInAccount?.[s.permission]) &&
                  s.id !== "last-visited"
              )
              .map((s) => {
                const active = section === `tools-for-${s.id}`;

                return (
                  <div
                    onClick={() => {
                      setSection(`tools-for-${s.id}`);
                    }}
                    className={twMerge(
                      "cursor-pointer",
                      "border border-solid border-[#e4eaf0] rounded-full",
                      "px-[16px] py-[8px]",
                      "text-[13px] font-medium leading-[1]",
                      active
                        ? "bg-[#0276be]"
                        : "bg-[#0276be19] hover:bg-gray-gray/40",
                      active ? "text-white" : "",
                      "flex items-center space-x-[6px]",
                      "whitespace-nowrap"
                    )}
                    key={s.name}
                  >
                    {s.icon && (
                      <span className={active ? "brightness-0 invert" : ""}>
                        {s.icon}
                      </span>
                    )}
                    <span>{s.name}</span>
                  </div>
                );
              })}
          </div>
        </div>
      );
    }
  }, [loggedInAccount, section, tab]);

  useEffect(() => {
    trackActivity("tools.list");
  }, [trackActivity]);

  useEffect(() => {
    if (
      tab !== "separator" &&
      tab?.permission &&
      !loggedInAccount?.[tab?.permission]
    ) {
      router.push("/tools");
    }
  }, [loggedInAccount, router, tab]);

  const disclaimer = useMemo(() => {
    return (
      <div className="text-[13px]">
        <span className="font-bold">Disclaimer:</span> While all reasonable care
        has been taken in the preparation of the Lab online tools, Smartkarma
        makes no assurance about the accuracy of any generated data or results.
        All data and results generated by the Lab online tools are indicative
        only and should be independently checked for accuracy and confirmed
        before use. Smartkarma accepts no responsibility for any loss or damage
        caused as a result of any inaccuracy or error within the Lab online
        tools or generated data.
      </div>
    );
  }, []);

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />

        {/* Open graph tags for social media etc */}
        {/* <meta property="og:image" content={coverImageUrl} /> */}
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      <div
        className={[
          "bg-gradient-to-br from-[#1d4ed8] to-[#06b6d4]",
          "bg-gradient-to-br from-[#24a9a7] to-[#4db364]",
          "bg-gradient-to-br from-[#7c3aed] to-[#c026d3]",
          "bg-gradient-to-br from-[#0f766e] to-[#0891b2]",
          "bg-gradient-to-br from-[#0d9488] to-[#65a30d]",
        ].join(" ")}
      />
      {media.mobile ? (
        <ToolsPageLayoutMobile>
          <div className="flex flex-col min-h-[calc(100vh-200px)]">
            {renderTabs}
            {renderSearch}
            <div className="flex-1">{!isSearchActive && children}</div>
            {disclaimer}
          </div>
        </ToolsPageLayoutMobile>
      ) : (
        <PaneLayout
          className="pl-[30px] py-[20px] min-h-[calc(100vh-170px)]"
          left={<ToolsPageTabs />}
          hideLeft
          hideRight
          hideGoToTop
        >
          <div className="flex flex-col min-h-[calc(100vh-170px)]">
            <div
              className={twMerge(
                "mt-[45px] md:pl-[15px]",
                "flex-1",
                "w-full",
                "flex flex-col"
              )}
            >
              {renderTabs}
              {renderSearch}
              <div className="flex-1">{!isSearchActive && children}</div>
            </div>
            {disclaimer}
          </div>
        </PaneLayout>
      )}
    </>
  );
};

export default ToolsPageLayout;
